import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { META_MAX, META_MIN } from "@/lib/admin/validacao";
import { comBarraFinal as comBarra, rotasPublicas } from "@/lib/rotas-fixas";

/** SEO das URLs fixas do site. A lista sai da própria árvore de rotas. */
async function rotasFixas(): Promise<string[]> {
  const { routeTree } = await import("@/routeTree.gen");
  return rotasPublicas(routeTree);
}

type LinhaSeo = {
  id?: string;
  path: string;
  title: string;
  description: string;
  robots: string;
};

export const Route = createFileRoute("/admin/seo")({
  ssr: false,
  head: () => ({
    meta: [{ title: "SEO das páginas | Painel" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: PainelSeo,
});

const campo =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring";

function PainelSeo() {
  const [linhas, setLinhas] = useState<LinhaSeo[] | null>(null);
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    void (async () => {
      const [caminhos, { data }] = await Promise.all([
        rotasFixas(),
        supabase.from("page_seo").select("id, path, title, description, robots"),
      ]);
      const porCaminho = new Map(
        (data ?? []).map((row) => [comBarra(row.path), row]),
      );
      const montadas: LinhaSeo[] = caminhos.map((path) => {
        const row = porCaminho.get(path);
        return {
          ...(row?.id ? { id: row.id } : {}),
          path,
          title: row?.title ?? "",
          description: row?.description ?? "",
          robots: row?.robots ?? "index,follow",
        };
      });
      setLinhas(montadas);
      setSelecionada((atual) => atual ?? montadas[0]?.path ?? null);
    })();
  }, []);

  const atual = useMemo(
    () => linhas?.find((l) => l.path === selecionada) ?? null,
    [linhas, selecionada],
  );

  function alterar(campoNome: "title" | "description" | "robots", valor: string) {
    setLinhas((lista) =>
      (lista ?? []).map((l) => (l.path === selecionada ? { ...l, [campoNome]: valor } : l)),
    );
  }

  async function salvar() {
    if (!atual) return;
    const tamanho = atual.description.trim().length;
    if (tamanho < META_MIN || tamanho > META_MAX) {
      setMensagem(
        `A meta description precisa ter entre ${META_MIN} e ${META_MAX} caracteres. Agora tem ${tamanho}.`,
      );
      return;
    }
    if (!atual.title.trim()) {
      setMensagem("O título é obrigatório.");
      return;
    }

    setSalvando(true);
    setMensagem(null);
    try {
      const valores = {
        path: atual.path,
        title: atual.title.trim(),
        description: atual.description.trim(),
        robots: atual.robots,
        updated_at: new Date().toISOString(),
      };
      if (atual.id) {
        const { error } = await supabase.from("page_seo").update(valores).eq("id", atual.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("page_seo")
          .insert(valores)
          .select("id")
          .single();
        if (error) throw error;
        setLinhas((lista) =>
          (lista ?? []).map((l) => (l.path === atual.path ? { ...l, id: data.id } : l)),
        );
      }
      setMensagem("Salvo. A página já sai com o novo título e a nova descrição.");
    } catch (erro) {
      setMensagem(`Não foi possível salvar: ${(erro as Error).message}`);
    } finally {
      setSalvando(false);
    }
  }

  if (linhas === null) {
    return <p className="text-sm text-muted-foreground">Carregando...</p>;
  }

  const tamanhoDescricao = atual?.description.trim().length ?? 0;
  const foraDaFaixa = tamanhoDescricao < META_MIN || tamanhoDescricao > META_MAX;

  return (
    <section>
      <h1 className="text-lg font-semibold text-foreground">SEO das páginas fixas</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Título, descrição e indexação de cada URL do site, sem mexer em código. O endereço
        canônico é gerado sozinho pelo próprio endereço da página.
      </p>

      <div className="mt-6 grid gap-6 md:grid-cols-[18rem_1fr]">
        <ul className="max-h-[32rem] overflow-y-auto rounded-md border border-border">
          {linhas.map((linha) => {
            const ativo = linha.path === selecionada;
            return (
              <li key={linha.path} className="border-b border-border last:border-b-0">
                <button
                  type="button"
                  onClick={() => {
                    setSelecionada(linha.path);
                    setMensagem(null);
                  }}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${
                    ativo ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/60"
                  }`}
                >
                  <span className="truncate">{linha.path}</span>
                  <span className="text-xs">{linha.title ? "ok" : "vazio"}</span>
                </button>
              </li>
            );
          })}
        </ul>

        {atual ? (
          <form
            className="space-y-4 rounded-md border border-border p-4"
            onSubmit={(e) => {
              e.preventDefault();
              void salvar();
            }}
            noValidate
          >
            <p className="text-sm font-medium text-foreground">{atual.path}</p>

            {mensagem ? (
              <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">
                {mensagem}
              </p>
            ) : null}

            <div>
              <label className="block text-sm font-medium text-foreground" htmlFor="titulo">
                Título da página
              </label>
              <input
                id="titulo"
                className={campo}
                value={atual.title}
                onChange={(e) => alterar("title", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground" htmlFor="descricao">
                Meta description
              </label>
              <textarea
                id="descricao"
                rows={3}
                className={campo}
                value={atual.description}
                onChange={(e) => alterar("description", e.target.value)}
              />
              <p
                className={`mt-1 text-xs ${foraDaFaixa ? "font-medium text-destructive" : "text-muted-foreground"}`}
              >
                {tamanhoDescricao} caracteres (precisa ficar entre {META_MIN} e {META_MAX})
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground" htmlFor="robots">
                Indexação
              </label>
              <select
                id="robots"
                className={campo}
                value={atual.robots}
                onChange={(e) => alterar("robots", e.target.value)}
              >
                <option value="index,follow">Aparecer no Google</option>
                <option value="noindex,nofollow">Não aparecer no Google</option>
              </select>
            </div>

            <Button
              type="submit"
              disabled={salvando}
            >
              {salvando ? "Salvando..." : "Salvar"}
            </Button>
          </form>
        ) : null}
      </div>
    </section>
  );
}
